/* eslint-disable complexity */
/* eslint-disable max-lines */
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getIsUserSignedIn } from "@gamedev-web/gamedev-sdk-core";

import { PageType } from "../constants";
import { IdXboxIntroFormId, IdXboxIntroSubFormsId } from "../constants/formConstants";
import {
  CrmApplicationStatus,
  CrmGameConceptStatus,
  CrmGameConceptStatusOrder,
} from "../models/crmStatus";
import {
  IdXboxGameConceptData,
  IdXboxIntroContactDataType,
  IdXboxIntroStudioDataType,
} from "../models/forms";
import { Programs } from "../models/programsConfig";
import { IdXboxApplicationStep, IdXboxApplicationStepsOrder } from "../models/status";
import { statusActions } from "../redux/actions/statusActions";
import { OnboardingRootState } from "../redux/reducers/rootReducer";
import {
  getCrmApplicationStatus,
  getGameConceptsList,
  getIdXboxApplicationStep,
  getIsFastTrackAccount,
  getIsGdkReceived,
  getIsInitialCrmFetchDone,
  getSubFormData,
} from "../redux/selectors";
import { GameConceptState, IdXboxGameConceptDataSet } from "../server/crm/api/models/forms";
import { validateGameConcept } from "../server/utils/dataValidation/dataFilter";
import { compareCrmApplicationStatus } from "./commonStatusHelpers";
import { isIdXboxIntroContactFormValid, isIdXboxIntroStudioFormValid } from "./formValidation";
import { getMostAdvancedGameConcept, isGameConceptSoftRejected } from "./gameConceptHelpers";

export const compareCrmGameConceptStatus = (
  sub1: CrmGameConceptStatus,
  sub2: CrmGameConceptStatus
) => {
  const indexSub1 = CrmGameConceptStatusOrder.indexOf(sub1);
  const indexSub2 = CrmGameConceptStatusOrder.indexOf(sub2);
  return indexSub1 < indexSub2 ? -1 : indexSub1 > indexSub2 ? 1 : 0;
};

export const compareIdXboxApplicationStepStatus = (
  step1: IdXboxApplicationStep,
  step2: IdXboxApplicationStep
) => {
  const indexStep1 = IdXboxApplicationStepsOrder.indexOf(step1);
  const indexStep2 = IdXboxApplicationStepsOrder.indexOf(step2);
  return indexStep1 < indexStep2 ? -1 : indexStep1 > indexStep2 ? 1 : 0;
};

export const checkGameInformationStep = async (
  step: IdXboxApplicationStep,
  crmGameConcept: (IdXboxGameConceptData & GameConceptState) | undefined
): Promise<IdXboxApplicationStep> => {
  if (step !== IdXboxApplicationStep.GameInformation || !crmGameConcept) {
    return step;
  }

  const crmGameConceptStatus = crmGameConcept.state.status;
  if (
    !crmGameConcept.details ||
    !crmGameConcept.gameExpectations ||
    !crmGameConcept.pricingAndAvailability ||
    !crmGameConcept.technicalCapabilities ||
    !crmGameConcept.configurations ||
    !crmGameConcept.xboxNetwork ||
    !crmGameConcept.ugc ||
    !crmGameConcept.supplementaryData
  ) {
    return step;
  }

  if (
    crmGameConceptStatus !== CrmGameConceptStatus.Unset &&
    crmGameConceptStatus !== CrmGameConceptStatus.MoreInfoNeeded &&
    !isGameConceptSoftRejected(crmGameConceptStatus, crmGameConcept.state.subStatus) &&
    crmGameConceptStatus !== undefined
  ) {
    return IdXboxApplicationStep.ApplicationReadyForReview;
  }

  const isGameConceptReadyForReview = await validateGameConcept(crmGameConcept);
  return isGameConceptReadyForReview ? IdXboxApplicationStep.ApplicationReadyForReview : step;
};

const checkApplicationReadyForReviewStep = (
  step: IdXboxApplicationStep,
  currentGameConceptStatus: CrmGameConceptStatus | undefined
): IdXboxApplicationStep => {
  if (step !== IdXboxApplicationStep.ApplicationReadyForReview || !currentGameConceptStatus) {
    return step;
  }

  const applicationBeingReviewedComparison = compareCrmGameConceptStatus(
    currentGameConceptStatus,
    CrmGameConceptStatus.UnderReview
  );

  return applicationBeingReviewedComparison >= 0
    ? IdXboxApplicationStep.ApplicationIsBeingReviewed
    : step;
};

const checkApplicationBeingReviewedStep = (
  step: IdXboxApplicationStep,
  currentGameConceptStatus: CrmGameConceptStatus | undefined,
  isGdkReceived: boolean
): IdXboxApplicationStep => {
  if (step !== IdXboxApplicationStep.ApplicationIsBeingReviewed || !currentGameConceptStatus) {
    return step;
  }

  switch (currentGameConceptStatus) {
    case CrmGameConceptStatus.AdditionalContractsSent:
      return IdXboxApplicationStep.AdditionalAgreements;
    case CrmGameConceptStatus.Accepted:
      return isGdkReceived
        ? IdXboxApplicationStep.ApplicationComplete
        : IdXboxApplicationStep.AdditionalAgreements;
    default:
      return step;
  }
};

export const calculateIdXboxApplicationStep = async (
  currentCrmIdXboxApplicationStatus: CrmApplicationStatus | undefined,
  gameConceptsList: IdXboxGameConceptDataSet,
  idXboxIntroContactSubFormData: IdXboxIntroContactDataType,
  idXboxIntroStudioSubFormData: IdXboxIntroStudioDataType,
  isGdkReceived: boolean
): Promise<IdXboxApplicationStep> => {
  /** ********************************** Steps logic ************************************/
  // NDA under review
  const isNdaUnderReview = currentCrmIdXboxApplicationStatus
    ? compareCrmApplicationStatus(
        currentCrmIdXboxApplicationStatus,
        CrmApplicationStatus.NdaUnderReview
      ) === 0
    : false;
  if (isNdaUnderReview) {
    return IdXboxApplicationStep.NdaUnderReview;
  }

  // NDA sent
  const isNdaSent = currentCrmIdXboxApplicationStatus
    ? compareCrmApplicationStatus(
        currentCrmIdXboxApplicationStatus,
        CrmApplicationStatus.NdaSent
      ) === 0
    : false;
  if (isNdaSent) {
    return IdXboxApplicationStep.NdaSent;
  }

  // NDA signed
  const isNdaSigned = currentCrmIdXboxApplicationStatus
    ? compareCrmApplicationStatus(
        currentCrmIdXboxApplicationStatus,
        CrmApplicationStatus.NdaSigned
      ) === 0
    : false;
  if (!isNdaSigned) {
    // Introduction (Contact + Studio) -> Intro ready to submit
    const isIdXboxContactFormValid = await isIdXboxIntroContactFormValid(
      idXboxIntroContactSubFormData
    );
    const isIdXboxStudioFormValid = await isIdXboxIntroStudioFormValid(
      idXboxIntroStudioSubFormData
    );
    return !isIdXboxContactFormValid || !isIdXboxStudioFormValid
      ? IdXboxApplicationStep.Introduction
      : IdXboxApplicationStep.IntroductionReadyToSubmit;
  }

  // NDA signed -> Game information
  let step = IdXboxApplicationStep.GameInformation;

  const mostAdvancedGameConcept = getMostAdvancedGameConcept(gameConceptsList);
  const mostAdvancedStatus = mostAdvancedGameConcept?.state.status;
  // Game information -> Application ready for review
  step = await checkGameInformationStep(step, mostAdvancedGameConcept);

  // Application ready for review -> Application being reviewed
  step = checkApplicationReadyForReviewStep(step, mostAdvancedStatus);

  // Application being reviewed -> Application approved / denied
  return checkApplicationBeingReviewedStep(step, mostAdvancedStatus, isGdkReceived);
};

export const useCalculateIdXboxStatus = async (pageType: PageType | undefined) => {
  const dispatch = useDispatch();

  const isSignedIn: boolean = useSelector(getIsUserSignedIn);
  const currentStep: IdXboxApplicationStep | undefined = useSelector(getIdXboxApplicationStep);
  const isGdkReceived: boolean = useSelector(getIsGdkReceived);
  const isInitialCrmFetchDone = useSelector(getIsInitialCrmFetchDone);

  // ID@Xbox Introduction sub form data
  const idXboxIntroContactSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, IdXboxIntroFormId, IdXboxIntroSubFormsId.ContactInformation)
  ) as IdXboxIntroContactDataType;
  const idXboxIntroStudioSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, IdXboxIntroFormId, IdXboxIntroSubFormsId.StudioInformation)
  ) as IdXboxIntroStudioDataType;

  // Application submission
  const currentCrmIdXboxApplicationStatus: CrmApplicationStatus | undefined = useSelector(
    getCrmApplicationStatus
  ) as CrmApplicationStatus | undefined;
  const gameConceptList = useSelector(getGameConceptsList) as IdXboxGameConceptDataSet;

  useEffect(() => {
    const calculateNextStep = async () => {
      // 0: Must be signed in
      if (!isSignedIn || pageType !== PageType.IdXboxApplication || !isInitialCrmFetchDone) {
        return;
      }

      const step = await calculateIdXboxApplicationStep(
        currentCrmIdXboxApplicationStatus,
        gameConceptList,
        idXboxIntroContactSubFormData,
        idXboxIntroStudioSubFormData,
        isGdkReceived
      );

      if (step !== currentStep) {
        dispatch(statusActions.updateStep(step, Programs.IdXbox));
      }
    };

    calculateNextStep();
  }, [
    dispatch,
    currentCrmIdXboxApplicationStatus,
    isSignedIn,
    currentStep,
    idXboxIntroContactSubFormData,
    idXboxIntroStudioSubFormData,
    gameConceptList,
    isGdkReceived,
    pageType,
    isInitialCrmFetchDone,
  ]);
};

export const useIsFastTrackAccountAndComplete = () => {
  const currentCrmIdXboxApplicationStatus = useSelector(getCrmApplicationStatus);
  const isFastTrackAccount = useSelector(getIsFastTrackAccount);
  const isGdkReceived = useSelector(getIsGdkReceived);

  return (
    currentCrmIdXboxApplicationStatus === CrmApplicationStatus.NdaSigned &&
    isFastTrackAccount &&
    isGdkReceived
  );
};
